import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';
import Layout from '../../components/layout/Layout';
import { format } from 'date-fns';
import { formatDate, formatTime as formatTimeRegional } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast } from 'react-hot-toast';
import authService from '../../services/authService';
import * as XLSX from 'xlsx';

// Helper function to format time using regional settings
const formatTime = (dateValue) => {
  if (!dateValue) return '-';
  return formatTimeRegional(dateValue);
};

// Helper to format payment method (handles JSON for split bills)
const renderPaymentMethod = (method) => {
  if (!method) return 'N/A';
  if (typeof method !== 'string') return String(method);

  if (method.trim().startsWith('{')) {
    try {
      const data = JSON.parse(method);
      const payments = data?.payments || [];
      if (payments.length === 0) return 'Split Bill';

      const labels = {
        CASH: 'Cash',
        PAYNOW: 'UPI',
        CREDIT_CARD: 'Card',
        DEBIT_CARD: 'Card',
        CREDIT: 'Credit',
        BANK_TRANSFER: 'Bank',
        CHEQUE: 'Cheque',
        PAYTM: 'Paytm',
        PHONEPE: 'PhonePe',
        GOOGLE_PAY: 'GPay'
      };

      const details = payments
        .map((p) => `${labels[p.mode] || p.mode}: ${formatCurrency(p.amount)}`)
        .join(', ');
      return `Split (${details})`;
    } catch (e) {
      return 'Split Bill';
    }
  }

  // Format single mode (e.g., CREDIT_CARD -> Credit Card)
  return method
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const LocationSalesReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    totalSales: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    totalProducts: 0,
    totalQuantitySold: 0,
    cashSales: 0,
    cardSales: 0,
    upiSales: 0,
    salesOrders: []
  });
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(authService.getUserRole() === 'ADMIN');
  const [userLocation, setUserLocation] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    initializeUserContext();
  }, []);

  // Fetch report data when filters change
  useEffect(() => {
    if (filtersReady) {
      fetchReportData();
    }
  }, [selectedLocation, startDate, endDate, filtersReady]);

  const initializeUserContext = async () => {
    try {
      const axios = (await import('../../utils/axios')).default;
      const response = await axios.get('/auth/me');
      const userData = response.data || {};
      const role = userData.role || authService.getUserRole();
      const adminFlag = role === 'ADMIN';
      const locationData = userData.location || null;

      setIsAdmin(adminFlag);
      setUserLocation(locationData);

      // Set default location based on user role
      if (adminFlag) {
        // Admin: default to empty string (All Locations)
        setSelectedLocation('');
      } else if (locationData?.id) {
        // Non-admin: default to their location
        setSelectedLocation(locationData.id.toString());
      }

      await fetchLocations({ adminFlag, locationData });
    } catch (error) {
      console.error('Error initializing user context:', error);
      // If error, still try to fetch locations and default to All Locations for admin
      const role = authService.getUserRole();
      const adminFlag = role === 'ADMIN';
      if (adminFlag) {
        setSelectedLocation('');
      }
      await fetchLocations({ adminFlag });
    } finally {
      setFiltersReady(true);
    }
  };

  const fetchLocations = async ({ adminFlag = isAdmin, locationData = userLocation } = {}) => {
    try {
      if (!adminFlag && locationData) {
        // Non-admin: only show their location
        setLocations([locationData]);
        return;
      }

      // Admin: fetch all locations
      const response = await axiosInstance.get(`${API_CONFIG.ENDPOINTS.LOCATIONS}?size=1000`);
      const locationsList = response.data?.content || response.data || [];

      if (!Array.isArray(locationsList)) {
        console.error('Locations response is not an array:', locationsList);
        setLocations([]);
        return;
      }

      // Remove duplicates based on name (case-insensitive)
      const uniqueLocations = [];
      const seenNames = new Set();

      locationsList.forEach(location => {
        if (location && location.id && location.name) {
          const normalizedName = location.name.toLowerCase().trim();
          if (!seenNames.has(normalizedName)) {
            seenNames.add(normalizedName);
            uniqueLocations.push(location);
          }
        }
      });

      console.log('Loaded locations:', uniqueLocations.length, uniqueLocations);
      setLocations(uniqueLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
      setLocations([]);
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = {
        startDate,
        endDate
      };
      // Send locationId parameter:
      // - If empty string (''), don't send it (backend treats null as "All Locations")
      // - If "warehouse", send "warehouse" string
      // - If a location ID, send the ID as string
      if (selectedLocation && selectedLocation !== '') {
        params.locationId = selectedLocation;
      }
      // If selectedLocation is empty string, don't include locationId in params
      // This will make backend receive null and show all locations

      const response = await axiosInstance.get(`${API_CONFIG.ENDPOINTS.REPORTS}/location-sales`, { params });

      setReportData({
        totalSales: response.data.totalSales || 0,
        totalOrders: response.data.totalOrders || 0,
        averageOrderValue: response.data.averageOrderValue || 0,
        totalProducts: response.data.totalProducts || 0,
        totalQuantitySold: response.data.totalQuantitySold || 0,
        cashSales: response.data.cashSales || 0,
        cardSales: response.data.cardSales || 0,
        upiSales: response.data.upiSales || 0,
        salesOrders: response.data.salesOrders || []
      });
    } catch (error) {
      console.error('Error fetching location sales report:', error);
      toast.error('Failed to load location sales report');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const response = await axiosInstance.get(`${API_CONFIG.ENDPOINTS.SALES_ORDERS}/${orderId}`);
      setSelectedOrder(response.data);
      setOrderDialogOpen(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load order details');
    }
  };

  const handleCloseOrderDialog = () => {
    setOrderDialogOpen(false);
    setSelectedOrder(null);
  };

  const handlePrintInvoice = async (orderId) => {
    try {
      const response = await axiosInstance.get(`/sales-orders/${orderId}/receipt`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      // Create a blob from the PDF stream
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `invoice-${orderId}.pdf`;

      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(link.href);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice. Please try again.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleExportToExcel = () => {
    try {
      setIsExporting(true);

      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Create a new worksheet
      const ws = {};

      // Set column widths
      ws['!cols'] = [
        { wch: 5 },   // S.No
        { wch: 18 },  // Order Number
        { wch: 14 },  // Date
        { wch: 12 },  // Time
        { wch: 26 },  // Location
        { wch: 28 },  // Customer
        { wch: 16 },  // Total Amount
        { wch: 18 },  // Payment Method
        { wch: 14 }   // Status
      ];

      let currentRow = 1;

      // Add title with date range
      const titleText = `Location Sales Report (${startDate} to ${endDate})`;
      XLSX.utils.sheet_add_aoa(ws, [[titleText]], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 16, color: { rgb: "1F4E79" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = [{ s: { c: 0, r: currentRow - 1 }, e: { c: 8, r: currentRow - 1 } }];
      currentRow += 2;

      // Add generation info
      XLSX.utils.sheet_add_aoa(ws, [['Generated on:', new Date().toLocaleString()]], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = { font: { bold: true } };
      currentRow += 1;

      // Add filter info if any filters are applied
      const activeFilters = [];
      if (selectedLocation) {
        const location = locations.find(l => l.id.toString() === selectedLocation.toString());
        if (location) activeFilters.push(`Location: ${location.name}`);
        else if (selectedLocation === 'warehouse') activeFilters.push('Location: Warehouse');
        else activeFilters.push('Location: All Locations');
      }

      if (activeFilters.length > 0) {
        XLSX.utils.sheet_add_aoa(ws, [['Filters:', activeFilters.join(', ')]], { origin: `A${currentRow}` });
        ws[`A${currentRow}`].s = { font: { bold: true } };
        currentRow += 1;
      }
      currentRow += 1;

      // Add summary section
      XLSX.utils.sheet_add_aoa(ws, [['SUMMARY']], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = ws[`!merges`] || [];
      ws[`!merges`].push({ s: { c: 0, r: currentRow - 1 }, e: { c: 8, r: currentRow - 1 } });
      currentRow += 1;

      // Summary data
      const summaryData = [
        ['Total Sales:', reportData.totalSales],
        ['Total Orders:', reportData.totalOrders],
        ['Average Order Value:', reportData.averageOrderValue],
        [''],
        ['Payment Method Breakdown:'],
        ['Cash Sales:', reportData.cashSales],
        ['Card Sales:', reportData.cardSales],
        ['UPI Sales:', reportData.upiSales]
      ];

      summaryData.forEach((row, idx) => {
        XLSX.utils.sheet_add_aoa(ws, [row], { origin: `A${currentRow + idx}` });
        // Style the labels
        ws[`A${currentRow + idx}`].s = { font: { bold: true } };
        // Style numeric values
        const cellRef = `B${currentRow + idx}`;
        if (ws[cellRef] && typeof row[1] === 'number') {
          // Currency values: Total Sales (0), Average Order Value (2), Cash Sales (5), Card Sales (6), UPI Sales (7)
          if (idx === 0 || idx === 2 || idx === 5 || idx === 6 || idx === 7) {
            ws[cellRef].s = { numFmt: '"₹"#,##0.00' };
          } else { // Regular numbers (Total Orders = 1)
            ws[cellRef].s = { numFmt: '#,##0' };
          }
        }
      });

      currentRow += summaryData.length + 2;

      // Add detailed report section
      XLSX.utils.sheet_add_aoa(ws, [['DETAILED SALES REPORT']], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = ws[`!merges`] || [];
      ws[`!merges`].push({ s: { c: 0, r: currentRow - 1 }, e: { c: 8, r: currentRow - 1 } });
      currentRow += 1;

      // Add headers
      const headers = ['S.No', 'Order Number', 'Date', 'Time', 'Location', 'Customer', 'Total Amount', 'Payment Method', 'Status'];
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${currentRow}` });

      // Style headers
      headers.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: currentRow - 1, c: colIndex });
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" }
        };
      });
      currentRow += 1;

      // Add data rows
      reportData.salesOrders.forEach((order, index) => {
        const row = [
          index + 1,
          order.orderNumber || 'N/A',
          order.orderDate ? formatDate(order.orderDate) : 'N/A',
          formatTime(order.createdAt),
          order.location?.name || 'Warehouse',
          order.customer?.name || 'Walk-in Customer',
          Number(order.total || 0),
          renderPaymentMethod(order.paymentMethod),
          order.status || 'N/A'
        ];

        XLSX.utils.sheet_add_aoa(ws, [row], { origin: `A${currentRow}` });

        // Style the Total Amount column as currency
        const amountCellRef = `G${currentRow}`;
        ws[amountCellRef].s = { numFmt: '"₹"#,##0.00' };

        currentRow += 1;
      });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Location Sales');

      // Generate filename with date range
      const filename = `Location_Sales_Report_${startDate}_to_${endDate}.xlsx`;

      // Save file with compression for better compatibility
      XLSX.writeFile(wb, filename, { compression: true });

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const paginatedSalesOrders = reportData.salesOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/dashboard"
            underline="hover"
            color="inherit"
          >
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            to="/reports"
            underline="hover"
            color="inherit"
          >
            Reports
          </Link>
          <Typography color="text.primary">Location Sales Report</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon fontSize="large" color="primary" />
            Location Sales Report
          </Typography>
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={isExporting || reportData.salesOrders.length === 0}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="location-select-label" shrink={selectedLocation !== '' || true}>Location</InputLabel>
                  <Select
                    labelId="location-select-label"
                    label="Location"
                    value={selectedLocation || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log('Location changed to:', newValue, 'Type:', typeof newValue);
                      setSelectedLocation(newValue);
                    }}
                    displayEmpty
                    disabled={!isAdmin}
                    inputProps={{
                      'aria-label': 'Location'
                    }}
                    renderValue={(value) => {
                      // Always return a value so the label shrinks
                      // Display "All Locations" when value is empty (default for admin)
                      if (!value || value === '' || value === null || value === undefined) {
                        return 'All Locations';
                      }
                      // Display "Warehouse" when warehouse is selected
                      if (value === 'warehouse' || String(value).toLowerCase() === 'warehouse') {
                        return 'Warehouse';
                      }
                      // Display location name for specific locations
                      const selectedLocationObj = locations.find(loc => {
                        return String(loc.id) === String(value);
                      });
                      if (selectedLocationObj) {
                        return selectedLocationObj.name;
                      }
                      // Fallback for non-admin users
                      if (!isAdmin && userLocation && String(value) === String(userLocation.id)) {
                        return userLocation.name;
                      }
                      return 'All Locations';
                    }}
                  >
                    {/* "All Locations" is NOT in dropdown - it's only shown in the field when value is empty */}
                    {/* Only show "Warehouse" for old companies (where "Main Location" doesn't exist) */}
                    {isAdmin && !locations.some(loc => loc.name?.toLowerCase().trim() === 'main location') && (
                      <MenuItem value="warehouse">Warehouse</MenuItem>
                    )}
                    {locations.length > 0 ? (
                      locations
                        .filter(location => {
                          // Filter out "Warehouse" to avoid duplicates with hardcoded option
                          // For new companies, "Main Location" is shown from the locations list
                          const name = location.name?.toLowerCase().trim();
                          return name !== 'warehouse';
                        })
                        .map((location) => (
                          <MenuItem key={location.id} value={String(location.id)}>
                            {location.name}
                          </MenuItem>
                        ))
                    ) : (
                      !isAdmin && userLocation && (
                        <MenuItem value={String(userLocation.id)}>
                          {userLocation.name}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchReportData}
                  disabled={loading}
                  sx={{ height: '56px' }}
                >
                  Apply Filter
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                      <Typography color="textSecondary" variant="subtitle2">
                        Total Sales
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {formatCurrency(reportData.totalSales)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ShoppingCartIcon color="primary" sx={{ mr: 1 }} />
                      <Typography color="textSecondary" variant="subtitle2">
                        Total Orders
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {reportData.totalOrders}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Payment Method Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AttachMoneyIcon color="success" sx={{ mr: 1 }} />
                      <Typography color="textSecondary" variant="subtitle2">
                        Cash Sales
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="success.main">
                      {formatCurrency(reportData.cashSales)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CreditCardIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography color="textSecondary" variant="subtitle2">
                        Card Sales
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="secondary.main">
                      {formatCurrency(reportData.cardSales)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccountBalanceWalletIcon color="info" sx={{ mr: 1 }} />
                      <Typography color="textSecondary" variant="subtitle2">
                        UPI Sales
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="info.main">
                      {formatCurrency(reportData.upiSales)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Sales Orders Table */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sales Orders Details
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell>Payment Method</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSalesOrders.length > 0 ? (
                        paginatedSalesOrders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.orderNumber || 'N/A'}</TableCell>
                            <TableCell>
                              {order.orderDate ? format(new Date(order.orderDate), 'dd-MM-yyyy') : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {formatTime(order.createdAt)}
                            </TableCell>
                            <TableCell>{order.location?.name || 'Warehouse'}</TableCell>
                            <TableCell>{order.customer?.name || 'Walk-in Customer'}</TableCell>
                            <TableCell align="right">{formatCurrency(order.total)}</TableCell>
                            <TableCell>{renderPaymentMethod(order.paymentMethod)}</TableCell>
                            <TableCell>
                              <Chip
                                label={order.status || 'N/A'}
                                color={
                                  order.status === 'COMPLETED' ? 'success' :
                                    order.status === 'PENDING' ? 'warning' :
                                      order.status === 'CANCELLED' ? 'error' : 'default'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleViewOrder(order.id)}
                                color="primary"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No sales orders found for the selected filters
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {reportData.salesOrders.length > rowsPerPage && (
                  <TablePagination
                    component="div"
                    count={reportData.salesOrders.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[rowsPerPage]}
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Order Details Dialog */}
        <Dialog
          open={orderDialogOpen}
          onClose={handleCloseOrderDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Order Details</Typography>
              <IconButton onClick={handleCloseOrderDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedOrder && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Order Number</Typography>
                    <Typography variant="body1">{selectedOrder.orderNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Order Date</Typography>
                    <Typography variant="body1">
                      {selectedOrder.orderDate ? format(new Date(selectedOrder.orderDate), 'dd-MM-yyyy') : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Location</Typography>
                    <Typography variant="body1">{selectedOrder.location?.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Customer</Typography>
                    <Typography variant="body1">{selectedOrder.customer?.name || 'Walk-in Customer'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Payment Method</Typography>
                    <Typography variant="body1">{renderPaymentMethod(selectedOrder.paymentMethod)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                    <Chip
                      label={selectedOrder.status || 'N/A'}
                      color={
                        selectedOrder.status === 'COMPLETED' ? 'success' :
                          selectedOrder.status === 'PENDING' ? 'warning' :
                            selectedOrder.status === 'CANCELLED' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>Order Items</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.product?.name || 'N/A'}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.subtotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="h6">
                    Total: {formatCurrency(selectedOrder.total)}
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOrderDialog}>Close</Button>
            {selectedOrder && (
              <Button
                variant="contained"
                onClick={() => {
                  handlePrintInvoice(selectedOrder.id);
                  handleCloseOrderDialog();
                }}
                startIcon={<PrintIcon />}
                color="primary"
              >
                Print Invoice
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default LocationSalesReport;

